/**
 * Resolves legacy in-page anchor links whose hash hard-codes a specific
 * Shopify theme template id, so they keep working when the same
 * `custom_liquid` content is rendered on a different theme version.
 *
 * Background
 * ----------
 * The 2025-* / 2026-* PDP `custom_liquid` sticky-nav blocks were authored
 * for the published RealCraft theme `132498587744`, where every PSB
 * section's outer DOM element gets an id like
 *   psb-template--17767742570592__psb_video_video_03_HgiemC
 *   shopify-section-template--17767742570592__pxu_slideshow_pjX8Ci
 * Their nav anchors target those exact ids:
 *   <a href="#psb-template--17767742570592__psb_video_video_03_HgiemC">VIDEO</a>
 * Whenever the same merchant content renders on a different theme version
 * (for example our local Horizon theme, where the template id is
 * `18297572917344`), the legacy ids never match anything and clicking a
 * tab does nothing.
 *
 * Strategy (minimal scope)
 * ------------------------
 * 1. Watch only anchors whose hash matches the legacy
 *    `<prefix>--<digits>__<handle>` pattern, where `<prefix>` is one of
 *    `psb-template` / `shopify-section-template`. Anything else is left
 *    to the browser's native handling.
 * 2. Resolve the live target via a portable attribute selector
 *    `[id$="__<handle>"]` — every Shopify section root id ends with
 *    `__<handle>` regardless of the theme template id.
 * 3. Smooth-scroll to the resolved element with a runtime-measured
 *    offset (header + sticky-nav heights), so we do not need to keep
 *    `scroll-margin-top` CSS in sync per template.
 * 4. Update `location.hash` to the resolved id so deep links and
 *    back/forward navigation stay consistent.
 * 5. Use a `MutationObserver` so anchors injected later (Section
 *    Rendering API, Alpine swaps, etc.) get the same treatment.
 */

(function () {
  'use strict';

  var LEGACY_HASH_PATTERN = /^(?:psb-template|shopify-section-template)--\d+__(.+)$/;
  var BOUND_FLAG = 'legacyAnchorBound';

  /** @param {string} hashRaw Hash without the leading `#`. */
  function resolveTarget(hashRaw) {
    if (!hashRaw) return null;

    // 1. If this hash exists verbatim on the page, prefer the direct match.
    //    Handles `#top`, `#woodspecies`, `#woodspeciesmobile`, etc., and
    //    also lets future merchant content "just work" once they switch
    //    to portable ids.
    var direct = document.getElementById(hashRaw);
    if (direct) return direct;

    // 2. Otherwise look for the legacy `<prefix>--<digits>__<handle>`
    //    shape and resolve via portable selector.
    var match = LEGACY_HASH_PATTERN.exec(hashRaw);
    if (!match) return null;

    var handle = match[1];
    var escaped = window.CSS && typeof window.CSS.escape === 'function'
      ? window.CSS.escape(handle)
      : handle.replace(/(["\\])/g, '\\$1');

    return document.querySelector('[id$="__' + escaped + '"]');
  }

  /** Measure the cumulative sticky offset at click time. */
  function getStickyOffset() {
    var headerEl = document.querySelector(
      'header-component, header.header, .section-header, [data-section-type*="header"]'
    );
    var headerH = headerEl ? headerEl.getBoundingClientRect().height : 0;

    var stickyNav = document.querySelector('.product-nav-container-main .nav-meat, .dropdown-sub');
    var stickyH = stickyNav ? stickyNav.getBoundingClientRect().height : 0;

    return headerH + stickyH + 12;
  }

  function handleClick(event) {
    var link = event.currentTarget;
    var href = link.getAttribute('href') || '';
    if (!href || href.charAt(0) !== '#') return;

    var hashRaw = href.slice(1);
    var target = resolveTarget(hashRaw);
    if (!target) return;

    event.preventDefault();
    var top = target.getBoundingClientRect().top + window.scrollY - getStickyOffset();
    window.scrollTo({ top: top, behavior: 'smooth' });

    try {
      history.pushState(null, '', '#' + (target.id || hashRaw));
    } catch (_) {
      // Same-origin pushState should always succeed; ignore if it ever fails.
    }
  }

  function bindAnchors(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var anchors = scope.querySelectorAll('a[href*="#"]');

    for (var i = 0; i < anchors.length; i++) {
      var link = anchors[i];
      if (link.dataset[BOUND_FLAG] === '1') continue;

      var href = link.getAttribute('href') || '';
      var hashIdx = href.indexOf('#');
      if (hashIdx < 0) continue;

      var hashRaw = href.slice(hashIdx + 1);
      if (!LEGACY_HASH_PATTERN.test(hashRaw)) continue;

      link.dataset[BOUND_FLAG] = '1';
      link.addEventListener('click', handleClick);
    }
  }

  function init() {
    bindAnchors(document);

    if (typeof MutationObserver === 'undefined') return;

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node && node.nodeType === 1) bindAnchors(node);
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
